import React, { useState } from "react";
import { toast } from "sonner";
import { ReceiptText, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="lg:w-1/2 bg-blue-700 text-white px-8 sm:px-16 py-14 flex flex-col justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-white/10 grid place-items-center">
            <ReceiptText className="h-5 w-5" />
          </div>
          <span className="font-display font-extrabold text-lg">FakturaEasy</span>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Rechnungen schreiben, ohne nachzudenken.
          </h1>
          <p className="mt-4 text-slate-300 text-sm leading-relaxed">
            Rechtssichere Rechnungen mit allen deutschen Pflichtangaben, fortlaufender Nummer,
            Kundendatenbank und PDF-Export – schlicht und schnell.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-slate-300">
            <li>✓ Automatische fortlaufende Rechnungsnummer</li>
            <li>✓ Frei anpassbare MwSt (19 %, 7 %, individuell)</li>
            <li>✓ Kleinunternehmerregelung nach § 19 UStG</li>
          </ul>
        </div>
        <p className="text-xs text-slate-500">© 2026 FakturaEasy · Prototyp</p>
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-sm">
          <img
            src="/fakturaeasy-logo.jpg"
            alt="FakturaEasy – simpel Rechnungen erstellen"
            data-testid="startpage-logo"
            className="h-28 w-auto mb-8 rounded-2xl shadow-sm ring-1 ring-slate-200"
          />
          <h2 className="font-display text-2xl font-bold tracking-tight">
            {mode === "login" ? "Willkommen zurück" : "Konto erstellen"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {mode === "login" ? "Melden Sie sich bei Ihrem Konto an." : "Starten Sie in wenigen Sekunden."}
          </p>

          <button
            onClick={googleLogin}
            data-testid="google-login-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-2.5 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <img alt="" className="h-4 w-4" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" />
            Mit Google anmelden
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" />
            oder mit E-Mail
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-3.5">
            {mode === "register" && (
              <Field icon={User} label="Name" testid="name-input" value={form.name} onChange={upd("name")} placeholder="Max Mustermann" required />
            )}
            <Field icon={Mail} label="E-Mail" testid="email-input" type="email" value={form.email} onChange={upd("email")} placeholder="name@firma.de" required />
            <Field icon={Lock} label="Passwort" testid="password-input" type="password" value={form.password} onChange={upd("password")} placeholder="••••••••" required />

            <button
              type="submit"
              disabled={busy}
              data-testid="submit-auth-button"
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
            >
              {busy ? "Bitte warten…" : mode === "login" ? "Anmelden" : "Registrieren"}
            </button>
          </form>

          <p className="mt-5 text-sm text-slate-500 text-center">
            {mode === "login" ? "Noch kein Konto? " : "Bereits registriert? "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="toggle-auth-mode"
              className="text-slate-900 font-medium hover:underline"
            >
              {mode === "login" ? "Jetzt registrieren" : "Anmelden"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1 relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid={testid}
          className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          {...props}
        />
      </div>
    </label>
  );
}
